import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-c9f442d8"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["src"]
const _hoisted_2 = ["src", "poster", "controls"]

import {
  ref, watch, onMounted, onUnmounted,
} from 'vue';
import { convertPotentialWeb3ProtocolToWeb2 } from '@/lib/util';
type SupportedMediaTypes = 'image' | 'video' | 'model' | 'unknown';

export default /*#__PURE__*/_defineComponent({
  __name: 'LazyMedia',
  props: {
    src: null,
    poster: null,
    showControls: { type: Boolean }
  },
  emits: ['load', 'error'],
  setup(__props: any, { emit }) {

const props = __props as {
  src: string,
  poster?: string;
  showControls?: boolean;
};





const show = ref(false);
const error = ref(false);
const mediaType = ref<SupportedMediaTypes>('unknown');
const optimizedMediaURL = ref('');
const lazyMediaContainer = ref<HTMLElement | null>(null);
const lazyMediaObserver = ref<IntersectionObserver | undefined>();

watch(show, () => {
  if (show.value) {
    emit('load');
  }
});

watch(error, () => {
  if (error.value) {
    emit('error');
  }
});

onMounted(() => {
  if ("IntersectionObserver" in window) {
    lazyMediaObserver.value = new IntersectionObserver(async (entries) => {
      for (let i = 0; i < entries.length; i++) {
        let entry = entries[i];
        if (entry.isIntersecting && entry.target.classList.contains("m-lazy-media")) {
          // @dev: process our media to understand it's content type
          // @dev: causes our first network requests and optimizedMediaURL to be set
          await processMedia(props.src);

          if (mediaType.value === 'unknown') {
            emit('error');
          }

          // @dev: disconnect the observer, as the media is now lazily-loaded
          if (lazyMediaObserver.value)
            lazyMediaObserver.value.disconnect();
        }
      }
    });

    // @dev: observe the element being moved on to screen
    if (lazyMediaContainer.value)
      lazyMediaObserver.value.observe(lazyMediaContainer.value as Element);
  }
});

onUnmounted(() => {
  // @dev: destroy our observer to free up processing power
  if (lazyMediaObserver.value)
    lazyMediaObserver.value.disconnect();
});

const processMedia = async (url: string): Promise<void> => {
  let optimizedUrl = getOptimizedURL(url);

  const response = await fetch(optimizedUrl, { method: 'HEAD' });
  const contenttype = response.headers.get('content-type');

  // Fall back to arweave if cloudinary doesn't work
  if (response.status !== 200) {
    optimizedUrl = url;
  }

  if (contenttype?.includes('video')) {
    mediaType.value = 'video';
  } else if (contenttype?.includes('model')) {
    mediaType.value = 'model';
  } else if (contenttype?.includes('img') || contenttype?.includes('image')) {
    mediaType.value = 'image';
  }

  optimizedMediaURL.value = optimizedUrl;
};

const getOptimizedURL = (url: string) => {
  let optimizedUrl = url;

  if (
    optimizedUrl.includes('cloudinary') ||
    optimizedUrl.includes('assets.manifoldxyz.workers.dev') ||
    optimizedUrl.includes('assets.manifold.xyz')
  ) {
    optimizedUrl = getOptimizedCloudinaryURL(optimizedUrl);
  }

  optimizedUrl = convertPotentialWeb3ProtocolToWeb2(optimizedUrl);

  return optimizedUrl;
};

const getOptimizedCloudinaryURL = (url: string) => {
  // @dev: refrain from optimizing already optimized links
  if ((url.includes('c_limit') || url.includes('c_thumb'))) {
    return url;
  }

  // cloudflare does not work for cloudinary right now
  if (url.includes('cloudinary')) {
    url = url.replace('res.cloudinary.com/manifoldxyz', 'assets.manifold.xyz');
  }
  if (url.includes('assets.manifoldxyz.workers.dev')) {
    url = url.replace('assets.manifoldxyz.workers.dev', 'assets.manifold.xyz');
  }
  const urlParts = url.split('/');
  const spliceLocation = urlParts.findIndex((url) => url === 'upload');
  urlParts.splice(spliceLocation + 1, 0, 'c_limit,q_80,w_1024');
  return urlParts.join('/');
};

return (_ctx: any,_cache: any) => {
  const _component_model_viewer = _resolveComponent("model-viewer")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["manifold m-lazy-media", { show: show.value }]),
    ref_key: "lazyMediaContainer",
    ref: lazyMediaContainer
  }, [
    (mediaType.value === 'image')
      ? (_openBlock(), _createElementBlock("img", {
          key: 0,
          src: optimizedMediaURL.value,
          onLoad: _cache[0] || (_cache[0] = ($event: any) => (show.value = true)),
          onError: _cache[1] || (_cache[1] = ($event: any) => (error.value = true))
        }, null, 40, _hoisted_1))
      : (mediaType.value === 'video')
        ? (_openBlock(), _createElementBlock("video", {
            key: 1,
            src: optimizedMediaURL.value,
            poster: __props.poster || '',
            autoplay: "",
            loop: "",
            muted: "",
            controls: __props.showControls,
            "webkit-playsinline": "",
            playsinline: "",
            disablePictureInPicture: "",
            disableRemotePlayback: "",
            onCanplaythrough: _cache[2] || (_cache[2] = ($event: any) => (show.value = true)),
            onLoadeddata: _cache[3] || (_cache[3] = ($event: any) => (show.value = true)),
            onError: _cache[4] || (_cache[4] = ($event: any) => (error.value = true))
          }, null, 40, _hoisted_2))
        : (mediaType.value === 'model')
          ? (_openBlock(), _createBlock(_component_model_viewer, {
              key: 2,
              src: optimizedMediaURL.value,
              autoplay: "",
              "seamless-poster": "",
              "shadow-intensity": "1",
              "camera-controls": "",
              onLoad: _cache[5] || (_cache[5] = ($event: any) => (show.value = true)),
              onError: _cache[6] || (_cache[6] = ($event: any) => (error.value = true))
            }, null, 8, ["src"]))
          : _createCommentVNode("", true)
  ], 2))
}
}

})